import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * PulsingDot — accent indicator with a soft halo, used to flag unseen
 * content (e.g. a HelpBubble whose topic the current user has not
 * yet opened, or a "new" badge on a nav item).
 *
 * Pencil: WithPulse state on HelpBubble (Vzk9b/nYOmw/ByRUR/p3CPv).
 *   - halo: 13×13 ellipse, accent at 25% alpha
 *   - dot:  9×9 solid accent
 *
 * Two concentric spans: outer halo with `animate-pulse` (Tailwind
 * easing-in-out 2s infinite), inner solid dot. Single Map lookup
 * pattern on the FE side keeps "is this unseen?" checks O(1) per
 * icon render — see the design-system "smarter data structures" rule.
 */
export const pulsingDotVariants = cva('relative inline-flex items-center justify-center', {
  variants: {
    tone: {
      green: '[--pd-color:var(--color-ia-green)]',
      brand: '[--pd-color:var(--color-ia-brand)]',
      info: '[--pd-color:var(--color-ia-info)]',
      warning: '[--pd-color:var(--color-ia-warning)]',
    },
    size: {
      sm: 'h-[10px] w-[10px]',
      md: 'h-[13px] w-[13px]',
      lg: 'h-[16px] w-[16px]',
    },
  },
  defaultVariants: { tone: 'green', size: 'md' },
})

export interface PulsingDotProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof pulsingDotVariants> {
  /** Accessible label describing what the dot is signalling. */
  label?: string
}

export const PulsingDot = React.forwardRef<HTMLSpanElement, PulsingDotProps>(
  ({ className, tone, size, label, ...props }, ref) => (
    <span
      ref={ref}
      role="status"
      aria-label={label ?? 'New'}
      className={cn(pulsingDotVariants({ tone, size }), className)}
      {...props}
    >
      {/* Halo — pulses opacity */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-pill animate-pulse opacity-40"
        style={{ backgroundColor: 'var(--pd-color)' }}
      />
      {/* Solid dot — fills ~70% of the container */}
      <span
        aria-hidden="true"
        className="relative rounded-pill h-[70%] w-[70%]"
        style={{ backgroundColor: 'var(--pd-color)' }}
      />
    </span>
  ),
)
PulsingDot.displayName = 'PulsingDot'
